// costanti.ts — valori fissi dell'app. Tutto ciò che si regola senza rilasciare l'app sta nelle
// impostazioni del server (chiavi app.*); qui solo ciò che serve prima di averle lette.
import { Capacitor } from "@capacitor/core";

export const MARCHIO = "Laughdown";
export const VERSIONE_APP: string = import.meta.env.VITE_VERSIONE ?? "000";

/** Indirizzo dell'API: vuoto sul web (stessa origine), assoluto nelle app native. */
export const BASE_API: string = (import.meta.env.VITE_URL_API ?? "").replace(/\/$/, "");

export type Piattaforma = "web" | "android" | "ios";
export const PIATTAFORMA: Piattaforma = ((): Piattaforma => {
  const p = Capacitor.getPlatform();
  return p === "android" || p === "ios" ? p : "web";
})();

// Valori di ripiego: quelli veri arrivano dalle impostazioni app.* (vedi IMP_APP) appena lette.
export const TIMEOUT_RICHIESTA_PREDEFINITO_SECONDI = 10;
export const RIPROVA_PREDEFINITA_SECONDI = 5;
export const SINCRONIZZA_PREDEFINITA_SECONDI = 15;
export const RICONNESSIONE_MASSIMA_PREDEFINITA_SECONDI = 30;
// Costanti tecniche dell'app (non regolabili dal pannello, con il motivo):
export const CAMPIONI_OROLOGIO = 8; // campioni per la stima dell'orologio: di più non migliora la precisione
export const RICONNESSIONE_MIN_MS = 1_000; // primo tentativo di ricollegamento, poi raddoppia
export const ERRORI_MAX_PER_SESSIONE = 5; // protezione contro valanghe di segnalazioni da un'app rotta

/** Chiavi delle preferenze salvate sul dispositivo (solo preferenze: il resto sta sul server). */
export const PREFERENZE = {
  tema: "ld.tema",
  aspetto: "ld.aspetto",
  lingua: "ld.lingua",
  nome: "ld.nome",
  dispositivo: "ld.dispositivo",
  sfide: "ld.sfide",
  disposizione: "ld.disposizione",
  suoni: "ld.suoni",
  filtro: "ld.filtro",
  sessione: "ld.sessione",
  connesso: "ld.connesso",
  nascita: "ld.nascita",
  tornei: "ld.tornei",
  /** Partite a 4 (v013): codice → token del mio posto. */
  gruppi: "ld.gruppi",
  /** Modalità di prova (tester): numeri del rilevamento in partita, video solo via TURN. */
  provaDiagnostica: "ld.prova_diagnostica",
  provaSoloTurn: "ld.prova_solo_turn",
  /** Avatar scelto (v019): numero; per chi ha l'account vale quello salvato sul server. */
  avatar: "ld.avatar",
  /** v021: versione della spiegazione dell'analisi del volto accettata su questo telefono */
  volto: "ld.volto",
  /** v021: colore primario scelto (temi/colori.ts) */
  colore: "ld.colore",
  /** v030: tutorial già visti su questo telefono: {"1v1": true, "torneo": true, "gruppo": true} */
  tutorial: "ld.tutorial",
  /** v035: video delle risate: «si» / «no» scelto una volta (null = mai chiesto); si manda da solo a ogni partita */
  clip: "ld.clip",
  /** v037: sì a mettere online i video in cui mi si vede («si» / «no»; null = mai chiesto) */
  online: "ld.online",
  /** v045: «Video per noi»: sì a lasciarci il video della risata per i nostri social e il sito («si» / «no») */
  noi: "ld.noi",
  /** v040: sì alle foto della partita in cui mi si vede («si» / «no») */
  foto: "ld.foto",
  /** v040: «Le tue scelte» fatte una volta su questo telefono ("1") */
  scelteFatte: "ld.scelte_fatte",
  /** v037: i video che ho messo online da questo telefono (codice + segreto per toglierli) */
  videoMiei: "ld.video_miei",
  /** v037: i giorni in cui ho giocato (per «giorni di fila»), AAAA-MM-GG, al massimo 60 */
  giorniGiocati: "ld.giorni",
  /** v040: il giorno (AAAA-MM-GG) in cui la «partita del giorno» è già stata festeggiata in Home */
  giornoFesteggiato: "ld.giorno_festeggiato",
  /** v037: testo più grande in tutta l'app («si») */
  testoGrande: "ld.testo_grande",
  /** v038: la striscia dei filtri della prova nello Store ridotta a una linea («si») */
  provaRidotta: "ld.prova_ridotta",
  // v045: ultima pagina «prima di giocare» con la pubblicità (ms), le scelte richieste in questa apertura, la guida
  pubblicitaPrima: "ld.pubblicita_prima",
  /** v045: l'ultimo filtro provato nello Store (l'obiettivo «ti mancano X ⭐» a fine partita) */
  filtroDesiderato: "ld.filtro_desiderato",
  /** v045: quando si è visto l'ultimo promemoria di «Le tue scelte» (ms) */
  promemoriaScelte: "ld.promemoria_scelte",
  /** v046: «filtro» e/o «voce» se si è già toccato il pulsante per cambiarli (finché no, pulsano) */
  toccati: "ld.toccati",
  /** v046: l'ultima voce scelta in partita */
  voce: "ld.voce",
  guidaVista: "ld.guida_vista",
  /** v038: spiegazioni già viste (torneo, partita a 4): JSON {"torneo":true,"gruppo":true} */
  spiegazioniViste: "ld.spiegazioni",
  /** v038: avatar/colore scelti ma non ancora salvati sul conto (si rimandano alla prossima apertura) */
  aspettoInSospeso: "ld.aspetto_in_sospeso",
  /** v038: notifiche accese su questo telefono («si»/«no»), l'indirizzo dell'iscrizione, la domanda in Home già fatta */
  notifiche: "ld.notifiche",
  notificheIndirizzo: "ld.notifiche_indirizzo",
  notificheDomanda: "ld.notifiche_domanda",
} as const;

/** Chiavi delle impostazioni pubbliche lette dall'app. */
export const IMP_APP = {
  manutenzione: "app.manutenzione",
  versioneMinima: "app.versione_minima_client",
  lingueAttive: "app.lingue_attive",
  linguaPredefinita: "app.lingua_predefinita",
  temiDisponibili: "app.temi_disponibili",
  temaPredefinito: "app.tema_predefinito",
  temiGratuiti: "app.temi_gratuiti",
  temiGold: "app.temi_gold",
  goldFiltriEsclusivi: "app.gold_filtri_esclusivi",
  sorpresaSecondi: "app.sorpresa_secondi",
  liberatoriaAttiva: "app.liberatoria_attiva",
  torneoAttivo: "app.torneo_attivo",
  torneoAggiornaSecondi: "app.torneo_aggiorna_secondi",
  gruppiAttivi: "app.gruppi_attivi",
  /** v038: partita a 4, video più leggero (kbit/s, 0 = nessun tetto) e altezza massima (px) */
  gruppiVideoKbps: "app.gruppi_video_kbps",
  /** v038: notifiche accese sul server */
  pushAttive: "app.push_attive",
  gruppiVideoAltezza: "app.gruppi_video_altezza",
  provaDaSoloAttiva: "app.prova_da_solo_attiva",
  gruppiVideoDistanzaMs: "app.gruppi_video_distanza_ms",
  gruppiIceRinnovoMinuti: "app.gruppi_ice_rinnovo_minuti",
  // clip della risata (v008)
  clipAttive: "app.clip_attive",
  /** v037: video online («Riesci a non ridere?», link /v/…) */
  videoOnline: "app.video_online",
  clipSecondiMassimi: "app.clip_secondi_massimi",
  clipSecondiPrimaRisata: "app.clip_secondi_prima_risata",
  clipSecondiRallentatore: "app.clip_secondi_rallentatore",
  clipRallentatorePercento: "app.clip_rallentatore_percento",
  clipSecondiFinale: "app.clip_secondi_finale",
  /** v040: tratto accelerato prima dei secondi normali (spento: video più fluido) */
  clipAccelerato: "app.clip_accelerato",
  clipAudio: "app.clip_audio",
  sorpresaRitardoMs: "app.sorpresa_ritardo_ms",
  riprovaSecondi: "app.rete_riprova_secondi",
  sincronizzaSecondi: "app.orologio_sincronizza_secondi",
  timeoutSecondi: "app.richiesta_timeout_secondi",
  riconnessioneMassimaSecondi: "app.riconnessione_massima_secondi",
  giocoAttivo: "app.gioco_attivo",
  disposizionePredefinita: "app.disposizione_predefinita",
  festaSecondi: "app.festa_secondi",
  rilevamentoAttivo: "app.rilevamento_attivo",
  rilevamentoFps: "app.rilevamento_fotogrammi_secondo",
  rilevamentoSoglia: "app.rilevamento_soglia",
  rilevamentoFotogrammi: "app.rilevamento_fotogrammi",
  rilevamentoSmorzamento: "app.rilevamento_smorzamento",
  rilevamentoPesoSorriso: "app.rilevamento_peso_sorriso",
  rilevamentoPesoBocca: "app.rilevamento_peso_bocca",
  rilevamentoPesoGuance: "app.rilevamento_peso_guance",
  rilevamentoSorrisoMinimo: "app.rilevamento_sorriso_minimo",
  rilevamentoRefrattarioMs: "app.rilevamento_refrattario_ms",
  rilevamentoVoltoAssenteMs: "app.rilevamento_volto_assente_ms",
  rilevamentoBoccaMinima: "app.rilevamento_bocca_minima",
  rilevamentoDurataMinimaMs: "app.rilevamento_durata_minima_ms",
  filtriRotazioneSecondi: "app.filtri_rotazione_secondi",
  linkPersonaleAttivo: "app.link_personale_attivo",
  amiciAttivi: "app.amici_attivi",
  richiesteControlloSecondi: "app.richieste_controllo_secondi",
  negozioAttivo: "app.negozio_attivo",
  classificaAttiva: "app.classifica_attiva",
  filtriRotazioneAvvisoSecondi: "app.filtri_rotazione_avviso_secondi",
  filtriAttivi: "app.filtri_attivi",
  filtriFps: "app.filtri_fotogrammi_secondo",
  filtriLatoMassimo: "app.filtri_lato_massimo",
  filtriQualitaAutomatica: "app.filtri_qualita_automatica",
  filtriRipristinoBocca: "app.filtri_ripristino_bocca",
  filtriMargineBocca: "app.filtri_margine_bocca",
  filtriAlzaTesta: "app.filtri_alza_testa",
  filtriTieniVoltoMs: "app.filtri_tieni_volto_ms",
  filtriStabilizzaTaglia: "app.filtri_stabilizza_taglia",
  filtriStabilizzaBeta: "app.filtri_stabilizza_beta",
  filtriErroriMassimi: "app.filtri_errori_massimi",
  filtriCatalogoCacheSecondi: "app.filtri_catalogo_cache_secondi",
  accountAttivi: "app.account_attivi",
  googleClientIdWeb: "app.accesso_google_client_id_web",
  appleServiceId: "app.accesso_apple_service_id",
  etaMinima: "app.eta_minima",
  goldInVendita: "app.piano_gold_in_vendita",
  acquistoWebLink: "app.acquisto_web_link",
  pubblicitaAttiva: "app.pubblicita_attiva",
  adsenseClient: "app.adsense_client",
  adsenseSlotRisultato: "app.adsense_slot_risultato",
  // v045: altri posti della pubblicità (Base) e quanto spesso la pagina «prima di giocare»
  adsenseSlotAttesa: "app.adsense_slot_attesa",
  adsenseSlotPrima: "app.adsense_slot_prima",
  /** v045: «Video per noi» acceso, guida della prima volta, luce sul «Provami» dello Store dopo N secondi fermi */
  videoNoi: "app.video_noi",
  guidaAttiva: "app.guida_attiva",
  storeLuceSecondi: "app.store_luce_secondi",
  /** v046: si parte già con una voce buffa (l'ultima scelta o una a caso) */
  voceIniziale: "app.voce_iniziale",
  pubblicitaPrimaOgniMinuti: "app.pubblicita_prima_ogni_minuti",
  pubblicitaPrimaSecondi: "app.pubblicita_prima_secondi",
  /** v046: AdMob nelle app (servizi/admob.ts): ID delle unità (vuoto = spento) e ID di prova di Google */
  admobBannerAndroid: "app.admob_banner_android",
  admobBannerIos: "app.admob_banner_ios",
  admobInterstizialeAndroid: "app.admob_interstiziale_android",
  admobInterstizialeIos: "app.admob_interstiziale_ios",
  admobProva: "app.admob_prova",
} as const;

export const PERCORSO_SFIDA = "/c/";
/** Torneo a coppie: /t/<codice> (lobby e tabellone), /t/<codice>/guarda (la finale per gli eliminati). */
export const PERCORSO_TORNEO = "/t/";
/** Partita a 4 a eliminazione (Gold): /g/<codice>. */
export const PERCORSO_GRUPPO = "/g/";
/** Strumenti di prova (solo tester con la modalità di prova accesa). */
export const PERCORSO_PROVA = "/prova";
/** Ogni quanto la pagina del torneo chiede lo stato, se manca app.torneo_aggiorna_secondi (limite: api.tornei_stato_minuto_per_ip). */
export const TORNEO_AGGIORNA_SECONDI = 3;
/** File del modello per il rilevamento del volto e del motore WebAssembly di MediaPipe, serviti dall'app stessa. */
export const MODELLO_VOLTO = "/modelli/face_landmarker.task";
export const CARTELLA_MEDIAPIPE = "/mediapipe";

export const PERCORSO_ADMIN = "/admin";
export const PERCORSO_ACCESSO = "/accesso";
export const PERCORSO_PROFILO = "/profilo";
export const PERCORSO_PIANI = "/piani";
export const PERCORSO_NEGOZIO = "/negozio";
export const PERCORSO_CLASSIFICA = "/classifica";
// v033: amici e «Disponibili ora» (sfida uno sconosciuto)
export const PERCORSO_AMICI = "/amici";
export const PERCORSO_DISPONIBILI = "/disponibili";
export const PERCORSO_LIBERATORIA = "/liberatoria";
/** Link personale: /@nome */
export const PERCORSO_PROFILO_PUBBLICO = "/@";
/** v037: il video delle risate messo online («Riesci a non ridere?») */
export const PERCORSO_VIDEO = "/v/";
export const PERCORSO_PRIVACY = "/legal/privacy";
export const PERCORSO_TERMINI = "/legal/termini";
export const PERCORSO_AIUTO = "/aiuto";
/** Scelta dell'avatar (v019). */
export const PERCORSO_AVATAR = "/scegli-avatar"; // non «/avatar»: è la cartella delle immagini (nginx darebbe 403)
export const PERCORSO_REGOLE = "/legal/regole";
